import React, { useMemo, useState } from "react";
import { X, Plus, RotateCcw, Flame } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useToast } from "../hooks/use-toast";
import { DEFAULT_WATCH_KEYWORDS } from "../lib/keywords";

/**
 * WatchlistEditor — admin-facing chip editor for the watchlist keywords used
 * by the Industry Alerts page and notifications bell. Persists via the settings
 * `patchAndSave` callback provided by the parent Settings screen.
 */
export default function WatchlistEditor({ settings, patchAndSave }) {
  const { toast } = useToast();
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const list = useMemo(() => {
    const l = settings?.news_watch_keywords;
    return Array.isArray(l) ? l : [];
  }, [settings]);

  const save = async (next) => {
    setBusy(true);
    try {
      await patchAndSave({ news_watch_keywords: next });
    } finally { setBusy(false); }
  };

  const addKeyword = async () => {
    const term = draft.trim();
    if (!term) return;
    if (list.some((k) => k.toLowerCase() === term.toLowerCase())) {
      toast({ title: "Already on the watchlist", description: term });
      setDraft("");
      return;
    }
    const next = [...list, term];
    setDraft("");
    await save(next);
  };

  const removeKeyword = async (kw) => {
    const next = list.filter((k) => k !== kw);
    await save(next);
  };

  const resetToDefault = async () => {
    if (!window.confirm("Replace the current watchlist with the built-in defaults?")) return;
    await save([...DEFAULT_WATCH_KEYWORDS]);
    toast({ title: "Watchlist reset to defaults" });
  };

  const missingDefaults = DEFAULT_WATCH_KEYWORDS.filter(
    (d) => !list.some((k) => k.toLowerCase() === d.toLowerCase())
  );

  return (
    <div className="space-y-3" data-testid="watchlist-editor">
      <p className="text-[12.5px] text-muted-foreground">
        Terms matched here paint Industry Alert rows orange and light up the
        bell whenever a new advisory mentions one. Matching is case-insensitive
        and boundary-aware — <code className="rounded bg-muted px-1 py-0.5 text-[11px]">Microsoft</code> won&apos;t match
        <code className="ml-1 rounded bg-muted px-1 py-0.5 text-[11px]">Microsoftware</code>.
      </p>

      <div className="flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
          placeholder="Add a product, vendor or system (e.g. Cisco Meraki)"
          className="h-9 text-[13px]"
          data-testid="watchlist-input"
        />
        <Button onClick={addKeyword} size="sm" className="h-9 gap-1.5" disabled={busy || !draft.trim()} data-testid="watchlist-add-btn">
          <Plus className="w-3.5 h-3.5" /> Add
        </Button>
        <Button onClick={resetToDefault} size="sm" variant="outline" className="h-9 gap-1.5" disabled={busy} data-testid="watchlist-reset-btn" title="Replace with the built-in list">
          <RotateCcw className="w-3.5 h-3.5" /> Reset
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-muted/20 p-3">
        <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2 inline-flex items-center gap-1.5">
          <Flame className="w-3 h-3 text-orange-500" /> Current watchlist · {list.length}
        </div>
        {list.length === 0 ? (
          <div className="text-[12.5px] text-muted-foreground italic">Watchlist is empty — advisories will still display, but nothing will get highlighted.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {list.map((kw) => (
              <span
                key={kw}
                className="group inline-flex items-center gap-1.5 rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-800 dark:text-orange-200 pl-2.5 pr-1 py-0.5 text-[11.5px] font-medium"
                data-testid={`watchlist-chip-${kw}`}
              >
                {kw}
                <button
                  type="button"
                  onClick={() => removeKeyword(kw)}
                  disabled={busy}
                  title={`Remove ${kw}`}
                  className="rounded-full p-0.5 hover:bg-orange-500/20 disabled:opacity-40"
                  data-testid={`watchlist-remove-${kw}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {missingDefaults.length > 0 && (
        <details className="rounded-lg border border-border bg-card p-3">
          <summary className="cursor-pointer text-[12px] font-medium text-muted-foreground hover:text-foreground">
            {missingDefaults.length} default term{missingDefaults.length === 1 ? "" : "s"} not yet on your list
          </summary>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {missingDefaults.map((kw) => (
              <button
                key={kw}
                type="button"
                disabled={busy}
                onClick={() => save([...list, kw])}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-background hover:border-primary/40 hover:text-primary px-2.5 py-0.5 text-[11.5px] font-medium"
                data-testid={`watchlist-suggest-${kw}`}
              >
                <Plus className="w-3 h-3" /> {kw}
              </button>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
